import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import heic2any from 'heic2any';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageSelected: (file: File) => void;
  onImageRemoved?: () => void;
  label?: string;
  maxSizeMB?: number;
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

const isHeic = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageSelected,
  onImageRemoved,
  label = "Choisir une image",
  maxSizeMB = 10,
}) => {
  const [preview, setPreview] = useState<string>(currentImageUrl || '');
  const [error, setError] = useState<string>('');
  const [converting, setConverting] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    // Check file type
    if (!ACCEPTED_TYPES.includes(file.type) && !isHeic(file)) {
      setError('Format non supporté. Utilisez JPG, PNG, WEBP ou HEIC');
      return;
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`L'image ne doit pas dépasser ${maxSizeMB}MB`);
      return;
    }

    // Convert HEIC → JPEG
    if (isHeic(file)) {
      setConverting(true);
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        const jpeg = Array.isArray(converted) ? converted[0] : converted;
        const jpegFile = new File(
          [jpeg],
          file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'),
          { type: 'image/jpeg' }
        );
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target?.result as string);
        reader.readAsDataURL(jpegFile);
        onImageSelected(jpegFile);
      } catch {
        setError('Erreur lors de la conversion HEIC. Essayez de convertir manuellement.');
      } finally {
        setConverting(false);
      }
      return;
    }

    // Standard image
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    onImageSelected(file);
  };

  const handleRemove = () => {
    setPreview('');
    setError('');
    if (onImageRemoved) onImageRemoved();
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preview ? (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Box
            component="img"
            src={preview}
            alt="Preview"
            sx={{
              width: 200,
              height: 200,
              objectFit: 'cover',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'divider',
            }}
          />
          <IconButton
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' },
            }}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={converting ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
          disabled={converting}
          sx={{ mb: 2 }}
        >
          {converting ? 'Conversion en cours...' : label}
          <input
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            onChange={handleFileChange}
          />
        </Button>
      )}

      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
        Formats acceptés : JPG, PNG, WEBP, HEIC • Taille max : {maxSizeMB}MB
      </Typography>
    </Box>
  );
};

export default ImageUpload;
